import { useContext } from "react";
import { ImportAddStepsAction } from "../actions";
import { AccessPointProcessingGroup, License, LicenseTypeResponse } from "../models";
import { ProviderNetworkImportContext } from "../providerNetworksImportContext";
import ImportConfigService from "../services/importConfigService";
import { ImportAddGroupState, ImportAddStepsState, ImportAddStepState } from "../state";

interface UseImportAddStepsReturnType {
  addSteps: ImportAddStepsState;
  clearSelected: () => void;
  displayAddSteps: boolean;
  getAvailableGroups: () => AccessPointProcessingGroup[];
  getGroup: (group: AccessPointProcessingGroup) => ImportAddGroupState;
  getGroupSteps: (group: AccessPointProcessingGroup) => ImportAddStepState[];
  groupSelected: (group: AccessPointProcessingGroup) => void;
  isAddStepsLoading: boolean;
  licenseFilterChanged: (license: License) => void;
  licenses: LicenseTypeResponse[];
  loadAvailableSteps: () => Promise<void>;
  loadLicenseTypes: () => Promise<void>;
  selectedGroups: () => AccessPointProcessingGroup[];
  selectedLicense: License;
  setDisplayAddSteps: (display: boolean) => void;
}

export function useImportAddSteps(): UseImportAddStepsReturnType {
  const { addStepsDispatch, addSteps } = useContext(ProviderNetworkImportContext);
  const importConfigService = new ImportConfigService();

  const loadAvailableSteps = async () => {
    addStepsDispatch({ type: ImportAddStepsAction.LoadAvailableSteps });
    const result = await importConfigService.getImportSteps();
    addStepsDispatch({ result, type: ImportAddStepsAction.LoadAvailableStepsSuccess });
  };

  const loadLicenseTypes = async () => {
    addStepsDispatch({ type: ImportAddStepsAction.LoadLicenseTypes });
    const result = await importConfigService.getLicenseTypes();
    addStepsDispatch({ result, type: ImportAddStepsAction.LoadLicenseTypesSuccess });
  };

  const groupSelected = (group: AccessPointProcessingGroup) => {
    addStepsDispatch({ group, type: ImportAddStepsAction.GroupSelected });
  };

  const clearSelected = () => {
    addStepsDispatch({ type: ImportAddStepsAction.ClearSelected });
  };

  const selectedGroups = () => {
    return Object.keys(addSteps.availableGroups).filter((key: AccessPointProcessingGroup) =>
      addSteps.availableGroups[key].selected) as AccessPointProcessingGroup[];
  };

  const getAvailableGroups = () => {
    return Object.keys(addSteps.availableGroups) as AccessPointProcessingGroup[];
  };

  const getGroup = (group: AccessPointProcessingGroup) => {
    return addSteps.availableGroups[group];
  };

  const getGroupSteps = (group: AccessPointProcessingGroup) => {
    return Object.values(addSteps.availableGroups[group].steps);
  };

  const licenseFilterChanged = (license: License) => {
    addStepsDispatch({ license, type: ImportAddStepsAction.LicenseFilterChanged });
  };

  const setDisplayAddSteps = (display: boolean) => {
    addStepsDispatch({ display, type: ImportAddStepsAction.AddStepsPanelDisplay });
  };

  const isAddStepsLoading = addSteps.isLoading;
  const displayAddSteps = addSteps.display;
  const licenses = addSteps.licenseTypes;
  const selectedLicense = addSteps.selectedLicense;

  return {
    addSteps,
    clearSelected,
    displayAddSteps,
    getAvailableGroups,
    getGroup,
    getGroupSteps,
    groupSelected,
    isAddStepsLoading,
    licenseFilterChanged,
    licenses,
    loadAvailableSteps,
    loadLicenseTypes,
    selectedGroups,
    selectedLicense,
    setDisplayAddSteps,
  };
}
